<template>
  <div class="primaryFont flex flex-row gap-4">
    <!-- Left Panel (Cards) -->
    <div class="leftPanel">
      <div class="flex flex-column gap-3">
        <CustomCardTab
          v-if="affectedStore.affectedList.length"
          v-for="affected in affectedStore.affectedList"
          :key="affected.ActuacionPersonaFisicaID"
          :status="affected.TipoRolPersonaFisicaID"
          :isSelected="affectedStore.isSelected(affected.ActuacionPersonaFisicaID)"
          :disabled="affectedStore.isCreate || affectedStore.isLoading || affectedStore.isEdit"
          @click="handleSelect(affected)"
        >
          <template #title>
            <span class="cardTitle">
              {{ affected.Nombre ? affected.Apellido + ", " + affected.Nombre : affected.Apellido }}
            </span>
          </template>

          <template #content>
            <span class="cardText">
              {{ affected.TipoRolPersonaFisicaID }}
            </span>
          </template>
        </CustomCardTab>
        <!-- Card simbólica para modo create -->
        <CustomCardTab
          v-if="affectedStore.isCreate && !affectedStore.isLoading"
          :key="'create-preview'"
          :status="affectedStore.form.TipoRolPersonaFisicaID?.nombre || 'Sin estado'"
          :isSelected="true"
          :isPreview="true"
          @click="() => {}"
        >
          <template #title>
            <div>
              {{
                affectedStore.form.nombre
                  ? affectedStore.form.apellido + ", " + affectedStore.form.nombre
                  : affectedStore.form.apellido || "Sin nombre"
              }}
            </div>
          </template>

          <template #content>
            <div>{{ affectedStore.form.TipoRolPersonaFisicaID?.nombre || "Sin estado" }}</div>
          </template>
        </CustomCardTab>
        <Button
          class="plusButton"
          label="Nuevo Afectado"
          icon="pi pi-plus"
          iconPos="left"
          @click="handleCreate"
          :disabled="affectedStore.isCreate || affectedStore.isLoading || affectedStore.isEdit"
        />
      </div>
    </div>
    <!-- Right Panel (Inputs) -->
    <div class="rightPanel ml-3 secondaryFont">
      <div>
        <div v-if="!affectedStore.affectedList.length && !affectedStore.isCreate">
          <p class="informativeText secondaryFont">
            No hay afectados en esta actuación. Cree un nuevo afectado
          </p>
        </div>

        <div v-else-if="affectedStore.affectedList">
          <!-- Caso 1: No hay persona seleccionada -->
          <p v-if="!selectedPersona" class="informativeText secondaryFont text-2xl">
            <b>Seleccione un afectado</b>
          </p>
          <!-- Caso 2: Orden público -->
          <p
            v-else-if="selectedPersona.Apellido === 'Orden público'"
            class="informativeText secondaryFont text-2xl"
          >
            <b>Orden público</b>
          </p>
          <!-- Caso 3: Mostrar el formulario -->
          <FormPersonaV2
            v-else
            personType="affected"
            :personData="selectedPersona"
            :isDisable="isDisabledRef === true ? isDisabledRef : isDisable"
            :key="selectedPersona?.ActuacionPersonaFisicaID || 'new'"
            @updatePerson="handleUpdatePerson"
            @validationStatus="handleValidationStatus"
            @setIsDisabled="(value) => setIsDisabled(value)"
            :isEdit="affectedStore.isEdit"
            :isCreate="affectedStore.isCreate"
            ref="formPersonaV2"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import CustomCardTab from "@/components/CustomCardTab.vue";
import FormPersonaV2 from "@/components/forms/FormPersonaV2.vue";
import { useRouterCompat } from "@/composables/index";
import { useAffectedStore } from "@/store/Pinia/useAffectedStore";

const props = defineProps({
  blockNavigationToAnotherTab: {
    type: Function,
    default: () => {},
  },
  removeTheBlockNavigationToAnotherTab: {
    type: Function,
    default: () => {},
  },
  formType: String,
  actuacionType: String,
});

const isDisabledRef = ref(false);

const isDisable = computed({
  get: () => {
    if (affectedStore.isCreate) return false;
    return isDisabledRef.value || !affectedStore.isEdit;
  },
  set: (value) => {
    isDisabledRef.value = value;
  },
});

const { route } = useRouterCompat();
const affectedStore = useAffectedStore();
const selectedPersona = computed({
  get: () => affectedStore.selected,
  set: (val) => {
    affectedStore.selected = val;
  },
});
const formPersonaV2 = ref(null);
defineExpose({
  formPersonaV2,
  selectedPersona,
});

const handleSelect = (affected) => {
  if (!affectedStore.isCreate) {
    affectedStore.select(affected);
    selectedPersona.value = affected;
  }
};
const handleCreate = () => {
  if (route.matched[0].path != "/newActuacion") {
    props.blockNavigationToAnotherTab();
  }
  affectedStore.startCreate();
  selectedPersona.value = affectedStore.form;
  isDisable.value = false;
  setTimeout(() => {
    formPersonaV2.value.sipeMode = true;
  }, 100);
};

onMounted(() => {
  selectedPersona.value = null;
  if (!affectedStore.isCreate && affectedStore.affectedList.length > 0) {
    selectedPersona.value = affectedStore.affectedList[0];
  }
});

const handleUpdatePerson = (person) => {
  person.PersonaFisicaID =
    selectedPersona?.value?.PersonaFisicaID !== undefined
      ? selectedPersona?.value?.PersonaFisicaID
      : null;
  person.ActuacionPersonaFisicaID =
    selectedPersona?.value?.ActuacionPersonaFisicaID !== undefined
      ? selectedPersona?.value?.ActuacionPersonaFisicaID
      : "";

  const isJuridicPersonValid = person.personaJuridica
    ? person.cuit &&
      person.denominacion &&
      person.domicilioJuridico &&
      person.telefonoJuridico &&
      person.rubro
    : true;

  affectedStore.form = {
    ...person,
    isValid: person.isValid && isJuridicPersonValid,
  };
  affectedStore.validateForm();
};

const handleValidationStatus = (errores) => {
  affectedStore.setValidationErrors(errores);
};
watch(
  () => affectedStore.form?.TipoRolPersonaFisicaID,
  (nuevo) => {
    affectedStore.handleRoleChange(nuevo);
  }
);
watch(
  () => affectedStore.isEdit,
  (newValue, oldValue) => {
    isDisable.value = !newValue;
  }
);

function setIsDisabled(value) {
  isDisabledRef.value = value;
}
</script>
<style scoped>
.customCard {
  border-radius: 8px;
  border-left: 6px solid #d97706;
  background-color: #eeeeee;
  box-shadow: 0px 4px 4px 0px #00000040;
  max-width: 448px;
  max-height: 104px;
  display: flex;
  justify-content: start;
  align-items: center;
}

.cardTitle {
  font-weight: 700;
  font-style: bold;
  font-size: 20px;
  color: black;
}

.cardText {
  font-weight: 700;
  font-style: bold;
  font-size: 16px;
  color: black;
}

>>> .p-card .p-card-body {
  width: 100% !important;
}

>>> .p-card .p-card-content {
  padding: 0;
}

.plusButton {
  background-color: #0b5195;
  border-radius: 6px;
  border: none;
  font-weight: 400;
  font-size: 16px;
}

.plusButton:focus {
  background-color: #0b5195;
  border: none;
}
.plusButton:hover {
  background-color: #0b519580;
  border-radius: 6px;
}

.leftPanel {
  max-width: 448px;
  flex: 1;
}

.rightPanel {
  flex: 2;
}

.informativeText {
  text-align: center;
  font-size: 28px;
  color: #0b5195;
  font-weight: 500;
}
</style>
